import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Content from "../../Components/Core/Content";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";
import FormInput from "../../Components/Core/FormInput";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import {
  getSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
} from "../../Services/SubscriptionService";
import "./SubscriptionPlansAdmin.css";

const emptyForm = {
  id: null,
  title: "",
  description: "",
  totalDay: "",
  price: "",
};

const SubscriptionPlansAdmin = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    document.title = "پلن‌ها و قیمت اشتراک";
    fetchPlans();
  }, []);

  const notify = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await getSubscriptionPlans();
      setPlans(data || []);
    } catch (error) {
      notify(error?.message || "خطا در دریافت پلن‌های اشتراک", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) =>
    new Intl.NumberFormat("fa-IR").format(amount ?? 0);

  const openCreate = () => {
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (plan) => {
    setForm({
      id: plan.id,
      title: plan.title || "",
      description: plan.description || "",
      totalDay: String(plan.totalDay ?? ""),
      price: String(plan.price ?? ""),
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    const title = form.title.trim();
    const totalDay = Number(form.totalDay);
    const price = Number(form.price);

    if (!title) {
      notify("عنوان پلن را وارد کنید.", "warning");
      return;
    }
    if (!Number.isInteger(totalDay) || totalDay <= 0) {
      notify("تعداد روز باید عدد صحیح بزرگ‌تر از صفر باشد.", "warning");
      return;
    }
    if (!Number.isInteger(price) || price < 0) {
      notify("قیمت باید عدد صحیح و صفر یا بیشتر باشد.", "warning");
      return;
    }

    const payload = {
      title,
      description: form.description.trim(),
      totalDay,
      price,
    };

    try {
      setSaving(true);
      if (form.id) {
        await updateSubscriptionPlan(form.id, payload);
        notify("پلن با موفقیت ویرایش شد.");
      } else {
        await createSubscriptionPlan(payload);
        notify("پلن جدید اضافه شد.");
      }
      setFormOpen(false);
      setForm(emptyForm);
      await fetchPlans();
    } catch (error) {
      notify(error?.message || "خطا در ذخیره پلن", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSubscriptionPlan(deleteId);
      setDeleteId(null);
      notify("پلن حذف شد.");
      await fetchPlans();
    } catch (error) {
      notify(error?.message || "خطا در حذف پلن", "error");
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid mb-4">
      <Content>
        <div className="plans-admin">
          <div className="plans-admin-header">
            <div>
              <h1 className="plans-admin-title">پلن‌ها و قیمت اشتراک</h1>
              <p className="plans-admin-subtitle">
                عنوان، مدت و قیمت مدل‌های اشتراک را از اینجا تغییر بده.
              </p>
            </div>
            <button
              type="button"
              className="plans-admin-add"
              onClick={openCreate}
            >
              <FontAwesomeIcon icon={faPlus} />
              پلن جدید
            </button>
          </div>

          {plans.length === 0 ? (
            <div className="plans-admin-empty">
              هنوز پلنی ثبت نشده. با دکمه «پلن جدید» یکی بساز.
            </div>
          ) : (
            <div className="plans-admin-grid">
              {plans.map((plan) => (
                <div key={plan.id} className="plans-admin-card">
                  <div className="plans-admin-card-head">
                    <div className="plans-admin-icon">
                      <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                    <div>
                      <div className="plans-admin-name">{plan.title}</div>
                      <div className="plans-admin-days">
                        {formatPrice(plan.totalDay)} روزه
                      </div>
                    </div>
                  </div>
                  <div className="plans-admin-price">
                    {formatPrice(plan.price)}
                    <span> تومان</span>
                  </div>
                  <div className="plans-admin-desc">
                    {plan.description || "توضیحی ثبت نشده است."}
                  </div>
                  <div className="plans-admin-actions">
                    <button
                      type="button"
                      className="plans-admin-btn edit"
                      onClick={() => openEdit(plan)}
                    >
                      <FontAwesomeIcon icon={faPen} />
                      ویرایش
                    </button>
                    <button
                      type="button"
                      className="plans-admin-btn delete"
                      onClick={() => setDeleteId(plan.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Content>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        className="plans-admin-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="plans-admin-dialog-title">
          {form.id ? "ویرایش پلن اشتراک" : "پلن جدید اشتراک"}
        </DialogTitle>
        <DialogContent>
          <FormInput
            name="عنوان"
            for="plan-title"
            required
            value={form.title}
            placeHolder="مثلاً اشتراک سه‌ماهه"
            onChange={(e, value) =>
              setForm((prev) => ({ ...prev, title: value ?? e.target.value }))
            }
          />
          <FormInput
            name="مدت (روز)"
            for="plan-days"
            required
            numericOnly
            value={form.totalDay}
            placeHolder="مثلاً ۹۰"
            onChange={(e, value) =>
              setForm((prev) => ({ ...prev, totalDay: value ?? e.target.value }))
            }
          />
          <FormInput
            name="قیمت (تومان)"
            for="plan-price"
            required
            numericOnly
            value={form.price}
            placeHolder="مثلاً ۱۵۰۰۰۰۰"
            onChange={(e, value) =>
              setForm((prev) => ({ ...prev, price: value ?? e.target.value }))
            }
          />
          <FormInput
            name="توضیحات"
            for="plan-desc"
            type="textarea"
            height="110px"
            value={form.description}
            placeHolder="توضیح کوتاه پلن"
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions className="plans-admin-dialog-actions">
          <Button className="plans-admin-cancel" onClick={closeForm}>
            انصراف
          </Button>
          <Button
            className="plans-admin-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "در حال ذخیره..." : "ذخیره"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        openDialog={deleteId != null}
        handleCloseDialog={() => setDeleteId(null)}
        handleConfirm={handleDelete}
        title="حذف پلن اشتراک"
        text={["این پلن از لیست تمدید حذف می‌شود. مطمئنی؟"]}
        cancleText="خیر"
        confirmText="حذف"
      />

      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </div>
  );
};

export default SubscriptionPlansAdmin;
